/**
 * Lectura del libro de Tricount (solo lectura, desde el dispositivo) y el último saldo conocido, para enseñarlo
 * aparte en «Tu dinero disponible» sin tener que leer el libro cada vez. En la demostración, datos inventados.
 */
import { readTricount, readSettlements, TRICOUNT, type Settlement, type TricountRow } from '@pf/workbook';
import { otherOf, tricountSummary } from '../app/tricount-model.js';
import { tricountApi, tricountBook } from './tricount-book.js';
import { tricountPayer } from './prefs.js';

export interface TricountData { rows: TricountRow[]; duplicates: TricountRow[] }
let mem: { at: number; data: TricountData } | null = null;

/** Lee el libro (con 2 minutos de memoria para no repetir lecturas al navegar). */
export async function loadTricount(force = false): Promise<TricountData> {
  if (!force && mem && Date.now() - mem.at < 120_000) return mem.data;
  const api = tricountApi();
  if (!api) throw new Error('Sin libro de Tricount');
  const data = await readTricount(api);
  mem = { at: Date.now(), data };
  return data;
}

/** Tras quitar un gasto: fuera de lo leído (la escritura va por la cola y puede tardar un momento). */
export function dropFromCache(row: TricountRow) {
  const out = (d: TricountData) => ({ ...d, rows: d.rows.filter(x => !(x.row === row.row && x.date === row.date && x.amount === row.amount && x.concept === row.concept)) });
  if (mem) mem = { ...mem, data: out(mem.data) };
  if (demo) demo = out(demo);
}
let demo: TricountData | null = null;
/** Demostración: los mismos datos inventados mientras la app está abierta (así se ve lo que quitas). */
export const demoData = (today: string) => (demo ??= demoTricount(today));

/** Último saldo calculado (lo guarda la pantalla de Tricount). El prefijo pf- hace que se borre al cambiar de usuario. */
export interface SavedBalance { me: string; other: string; balance: number; since: string | null; at: string }
const KEY = 'pf-tricount-saldo';
export function savedBalance(): SavedBalance | null {
  try { return JSON.parse(localStorage.getItem(KEY) ?? 'null') as SavedBalance | null; } catch { return null; }
}
export function saveBalance(b: SavedBalance) {
  try { localStorage.setItem(KEY, JSON.stringify(b)); } catch { /* sin almacenamiento */ }
}
export const balanceIsStale = (b: SavedBalance | null) => !b || Date.now() - Date.parse(b.at) > 6 * 3600_000;

/** Demostración: unos meses de gastos en común inventados, hasta «hoy». */
export function demoTricount(today: string): TricountData {
  const concepts: Array<[string, number, 'Raul' | 'Pilar', string]> = [
    ['Mercadona', 84.2, 'Raul', 'Cuenta'], ['Cena sushi', 58, 'Pilar', 'Tickets'], ['Gasolina', 60, 'Raul', 'Cuenta'], ['Carrefour', 112.4, 'Raul', 'Cuenta'],
    ['Cine', 19, 'Pilar', 'Efectivo'], ['Farmacia', 23.5, 'Raul', 'Cuenta'], ['Pizzería', 42, 'Raul', 'Tickets'], ['Frutería', 18.3, 'Pilar', 'Efectivo'],
    ['Mercadona', 96.8, 'Raul', 'Cuenta'], ['Regalo cumpleaños', 45, 'Pilar', 'Cuenta'],
  ];
  const end = Date.parse(today), rows: TricountRow[] = [];
  // Un gasto cada 5 días durante unos 7 meses
  for (let i = 0, d = end - 2 * 86400000; d > end - 215 * 86400000; i++, d -= 5 * 86400000) {
    const [concept, amount, payer, origin] = concepts[i % concepts.length]!;
    rows.push({ date: new Date(d).toISOString().slice(0, 10), concept, amount: Math.round(amount * (0.85 + ((i * 37) % 30) / 100) * 100) / 100, payer, origin, row: 6 + i });
  }
  const dupOf = rows[3]!;
  return { rows, duplicates: [{ ...dupOf, row: 6 }] };
}

/** Saldo para enseñarlo aparte en «Tu dinero disponible»: el último guardado (en la demostración, el inventado). */
export function knownBalance(demo: boolean, today: string): { other: string; balance: number } | null {
  if (demo) { const d = demoTricount(today); return tricountSummary(d.rows, 'Raul', 'Pilar', today); }
  const b = savedBalance();
  return b && b.me === tricountPayer() ? b : null;
}

/** Si el saldo guardado tiene más de 6 horas, vuelve a leer el libro (en segundo plano) y devuelve el nuevo; si no, null. */
export async function refreshBalanceIfStale(today: string): Promise<SavedBalance | null> {
  const me = tricountPayer();
  if (!tricountBook() || !me || !balanceIsStale(savedBalance())) return null;
  const { rows } = await loadTricount();
  const other = otherOf(me, rows, TRICOUNT.payers);
  const sum = tricountSummary(rows, me, other, today);
  const b = { me, other, balance: sum.balance, since: sum.since, at: new Date().toISOString() };
  saveBalance(b);
  return b;
}

/** Saldos anteriores (pestaña «Saldos» del libro de Tricount). En la demostración, los hechos en esta sesión. */
let demoSettlements: Settlement[] = [];
let settlementsMem: { at: number; list: Settlement[] } | null = null;
export async function loadSettlements(demoMode: boolean, force = false): Promise<Settlement[]> {
  if (demoMode) return demoSettlements;
  if (!force && settlementsMem && Date.now() - settlementsMem.at < 120_000) return settlementsMem.list;
  const api = tricountApi();
  if (!api) return [];
  const { settlements } = await readSettlements(api);
  settlementsMem = { at: Date.now(), list: settlements };
  return settlements;
}
/** Tras saldar (o apuntar un saldo): lo leído se actualiza al momento; la escritura va por la cola. */
export function afterSettle(s: Settlement, clearRows: boolean) {
  if (clearRows) { if (mem) mem = { ...mem, data: { ...mem.data, rows: [] } }; if (demo) demo = { ...demo, rows: [] }; }
  const add = (list: Settlement[]) => [s, ...list.filter(x => x.id !== s.id)].sort((a, b) => b.date.localeCompare(a.date));
  demoSettlements = add(demoSettlements);
  if (settlementsMem) settlementsMem = { ...settlementsMem, list: add(settlementsMem.list) };
}
